export const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

export type Days = {
  [key in (typeof DAYS)[number]]: string;
};

export interface CronScheduleForm {
  monday?: string;
  tuesday?: string;
  wednesday?: string;
  thursday?: string;
  friday?: string;
  saturday?: string;
  sunday?: string;
}

export class CronConverter {
  static GetDayTimeFromCronSchedules(day: keyof Days, schedules: string[]): string | undefined {
    // The cron shcedule is in the format '0 0 * * 0' or '0 0 * * *'
    // We want to extract the time part, which is the second element
    const dayNumber = DAYS.indexOf(day).toString();
    // Find the schedule whose day of the week matches the day we are looking for
    const schedule = schedules.find((s) => {
      if (!CronConverter.IsValidCronSchedule(s)) {
        return false;
      }
      const day = s.split(' ')[4];
      return day === dayNumber || day === '*';
    });

    if (!schedule) {
      return undefined;
    }

    const [minute, hour] = schedule.split(' ');

    return `${this.padNumber(hour)}:${this.padNumber(minute)}:00`;
  }

  private static padNumber(num: string): string {
    // Pad the number with leading zeros if it's less than 10
    return num.length < 2 ? `0${num}` : num;
  }

  static IsValidCronSchedule(schedule: string): boolean {
    // The schedule must be in the format '0 0 * * 0' or '0 0 * * *'
    const parts = schedule.split(' ');
    if (parts.length !== 5) {
      return false;
    }
    if ([parts[0], parts[1]].some((part) => isNaN(parseInt(part)))) {
      return false;
    }
    if (parts[2] !== '*' || parts[3] !== '*') {
      return false;
    }
    if (parts[4] !== '*' && (parseInt(parts[4]) < 0 || parseInt(parts[4]) > 6)) {
      return false;
    }
    return true;
  }

  static GetCronSchedulesFromForm(form: CronScheduleForm): string[] {
    // If all 7 days have the same time, we can simplify the cron schedule
    const allDaysHaveSameTime = DAYS.every((day) => !!form[day] && form[day] === form[DAYS[0]]);
    if (allDaysHaveSameTime) {
      return [CronConverter.GetCronScheduleFromDayTime('*', form[DAYS[0]] as string)];
    }
    // Otherwise, we need to create a cron schedule for each day
    return DAYS.reduce((acc, day) => {
      if (!form[day]) {
        return acc;
      }
      return [...acc, CronConverter.GetCronScheduleFromDayTime(day, form[day] as string)];
    }, [] as string[]);
  }

  private static GetCronScheduleFromDayTime(day: keyof Days | '*', time: string): string {
    // The cron shcedule is in the format '0 0 * * 0' or '0 0 * * *'
    // We want to extract the time part, which is the first and second element
    const [hour, minute] = time.split(':');
    const dayNumber = day === '*' ? '*' : DAYS.indexOf(day).toString();
    return `${minute} ${hour} * * ${dayNumber}`;
  }
}
